import { useEffect } from 'react';

import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { NotificationService } from 'services/notification-service';
import { VoiceService } from 'services/voice-service';
import { DEFAULT_TRANSCRIPTION_CONFIG, DEFAULT_VOICE_CONFIG } from 'shared/constants/phone-smart-assistant';
import { VOICES_QUERY_KEY } from 'shared/constants/query-keys';
import {
  ConfigurationFormValues,
  PhoneAgentBasicsFormValues,
  SmartAssistant,
  SmartAssistantModel,
  TelephonyFormValues
} from 'shared/interfaces/SmartAssistant';
import { VoiceConfiguration } from 'shared/interfaces/Voice';
import {
  CUSTOM_PROMPT_MAX_LENGTH,
  maxLengthValidator,
  requiredInputValidator,
  transcriptionConfigurationValidator,
  voiceConfigurationValidator
} from 'shared/utils/form-validators';

export function usePhoneSmartAssistantForms(smartAssistant?: SmartAssistant) {
  const allVoicesQuery = useQuery({
    queryKey: [VOICES_QUERY_KEY],
    queryFn: VoiceService.getVoices,
    onError: () => NotificationService.showResult(false, 'FEED_BACK.VOICE.FAILURE.GET_VOICES')
  });

  const phoneAgentBasicsForm = useForm({
    initialValues: {
      name: smartAssistant?.name ?? '',
      welcomeMessage: smartAssistant?.welcomeMessage ?? '',
      voiceConfiguration: smartAssistant?.voiceIntegration?.voiceConfiguration ?? DEFAULT_VOICE_CONFIG,
      transcriptionConfiguration:
        smartAssistant?.voiceIntegration?.transcriptionConfiguration ?? DEFAULT_TRANSCRIPTION_CONFIG
    } as PhoneAgentBasicsFormValues,
    validate: {
      name: (value) => requiredInputValidator(value),
      voiceConfiguration: (value) => voiceConfigurationValidator(value),
      transcriptionConfiguration: (value) => transcriptionConfigurationValidator(value)
    }
  });

  const configurationForm = useForm<ConfigurationFormValues>({
    initialValues: {
      customSystemPrompt: smartAssistant?.customSystemPrompt ?? '',
      model: smartAssistant?.model ?? SmartAssistantModel.GPT_4_O_REALTIME,
      restrictAnswersToResourcesContent: smartAssistant?.restrictAnswersToResourcesContentResponse ? true : false,
      restrictAnswersToResourcesContentResponse: smartAssistant?.restrictAnswersToResourcesContentResponse ?? ''
    } as ConfigurationFormValues,
    validate: {
      customSystemPrompt: (value) =>
        requiredInputValidator(value) || maxLengthValidator(value, CUSTOM_PROMPT_MAX_LENGTH),
      model: (value) => requiredInputValidator(value),
      restrictAnswersToResourcesContentResponse: (value, values) =>
        values.restrictAnswersToResourcesContent ? requiredInputValidator(value) : null
    }
  });

  const telephonyForm = useForm<TelephonyFormValues>({
    initialValues: {
      telephonyConfiguration: smartAssistant?.voiceIntegration?.telephonyConfiguration ?? undefined
    } as TelephonyFormValues
  });

  // Find matching voice and replace the voice configuration to get the signed preview url
  useEffect(() => {
    let voice: VoiceConfiguration | undefined;
    const isManagingSmartAssistant = !!smartAssistant;
    if (isManagingSmartAssistant && allVoicesQuery.data) {
      voice = allVoicesQuery.data.find(
        (voice) =>
          voice.voiceId === smartAssistant.voiceIntegration?.voiceConfiguration?.voiceId &&
          voice.provider === smartAssistant.voiceIntegration?.voiceConfiguration?.provider
      );
    } else if (allVoicesQuery.data) {
      voice = allVoicesQuery.data.find(
        (voice) =>
          voice.voiceId === phoneAgentBasicsForm.values.voiceConfiguration.voiceId &&
          voice.provider === phoneAgentBasicsForm.values.voiceConfiguration.provider
      );
    }

    if (voice && !phoneAgentBasicsForm.values.voiceConfiguration.previewUrl) {
      phoneAgentBasicsForm.setFieldValue('voiceConfiguration', voice);

      // This is not a user change, so we don't want to make the form dirty
      phoneAgentBasicsForm.resetDirty({ ...phoneAgentBasicsForm.values, voiceConfiguration: voice });
    }
  }, [allVoicesQuery.data, smartAssistant]);

  return { phoneAgentBasicsForm, configurationForm, telephonyForm };
}
